import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Inline, ProgressBar, Stack, t, Text } from '@kanbios/ui';
import { api, unwrap } from '../../lib/api';
import { useEnrolmentJob } from './EnrolmentProgress';
import { EnrolmentDrawer } from '../../pages/settings/EnrolmentDrawer';

const ACTIVE = new Set(['queued', 'running']);
const TONES = { queued: 'neutral', running: 'info', succeeded: 'pos', failed: 'neg' } as const;
/** D43-2: after the run, what Checkmk says (the run ending is a step, these are the result). */
const OBSERVED = ['AgentReachable', 'ChecksReceived'] as const;
const OBSERVED_TONES = { True: 'pos', False: 'neutral', Unknown: 'neutral' } as const;

/**
 * D38: the server's enrolment in its page's band (PageHeader `extra`), while
 * it runs and once it ended on this page: the task under way, a ProgressBar of
 * the tasks done, « Suivre les tâches » opens the follow-up (its Timeline).
 */
export function EnrolmentBand({ server, jobs, onFinished }: {
  server: string;
  /** the server's enrolments, latest first (configuration endpoint) */
  jobs: { id: string; status: string }[] | undefined;
  /** the enrolment just ended: the page reloads what it changed */
  onFinished: () => void;
}) {
  const [followed, setFollowed] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const active = jobs?.find(j => ACTIVE.has(j.status))?.id ?? null;
  useEffect(() => {
    if (active) setFollowed(active);
  }, [active]);
  const job = useEnrolmentJob(followed);
  const progress = job.data?.servers.find(s => s.server === server);
  const ended = Boolean(job.data && !ACTIVE.has(job.data.status));
  // onFinished changes on each render of the page: only the end counts.
  const finished = useRef(onFinished);
  finished.current = onFinished;
  useEffect(() => {
    if (ended) finished.current();
  }, [ended]);
  // D43-2: once the run succeeded, Checkmk reading the agent and its first checks, read every 10 s until both are there.
  const observed = useQuery({
    queryKey: ['server-synchronisation', server],
    queryFn: async () => unwrap(await api.fleetState.GET('/servers/{server}/synchronisation', { params: { path: { server } } })),
    enabled: progress?.status === 'succeeded',
    retry: false,
    refetchInterval: query => (OBSERVED.every(type => query.state.data?.conditions.find(c => c.type === type)?.status === 'True') ? false : 10_000),
  });
  if (!followed || !progress) return null;

  const current = progress.tasks.find(task => task.status === 'current' || task.status === 'error');
  const caption = progress.status === 'queued' ? t('enrolmentBand.queued')
    : progress.status === 'running' ? t('enrolmentBand.running', { task: current?.label ?? '' })
      : progress.status === 'succeeded' ? t('enrolmentBand.succeeded')
        : t('enrolmentBand.failed', { task: current?.label ?? '' });
  return (
    <>
      <Stack gap={8}>
        <Inline gap={12} align="center" wrap>
          <Badge tone={TONES[progress.status]}>{t(`settings.enrolment.result.${progress.status}`)}</Badge>
          <Text variant="body-sm" tone="ink-2">{caption}</Text>
          <Button variant="ghost" size="sm" icon="list" onClick={() => setOpen(true)}>{t('enrolmentBand.follow')}</Button>
        </Inline>
        {progress.status === 'succeeded' && observed.data && (
          <Inline gap={8} wrap>
            {OBSERVED.map(type => {
              const status = observed.data.conditions.find(c => c.type === type)?.status ?? 'Unknown';
              return <Badge key={type} tone={OBSERVED_TONES[status]} dot={status !== 'True'}>{t(`enrolmentBand.observed.${type}.${status === 'True' ? 'done' : 'awaited'}`)}</Badge>;
            })}
          </Inline>
        )}
        <ProgressBar
          size="lg"
          value={(progress.done / progress.total) * 100}
          label={t('settings.enrolment.tasksDone', { n: progress.done, total: progress.total })}
          ariaLabel={t('enrolmentBand.label')}
        />
      </Stack>
      <EnrolmentDrawer jobId={open ? followed : null} onClose={() => setOpen(false)} onRetry={() => undefined} canRetry={false} />
    </>
  );
}
